<template>
  <OmtCard data-cy="refund-detail-collapsable-card">
    <CFlex>
      <StatusTile v-bind="getHeader()" :isTotalVisible="false" />
    </CFlex>

    <CCollapse :isOpen="isOpen">
      <CBox mt="-15px" pt="16px">
        <CDivider mt="14px" mb="16px" />

        <!-- Seção de motivo da recusa -->
        <CBox v-if="refundDetail.status === 'D'" mb="16px">
          <OmtCard bg="var(--colors-light-primary-light-gray)" border="0" border-radius="8px">
            <CFlex align-items="center" mb="16px">
              <template v-if="isDenied">
                <img :src="IconOmint" class="img-omint" />
              </template>

              <OmtIcons v-else name="circle-exclamation" size="1x" mr="2" />
              <OmtTypography as="h3-bold" color="var(--colors-light-primary-gray1)">Motivo da recusa:</OmtTypography>
            </CFlex>
            <OmtTypography as="p1" color="var(--colors-light-primary-gray1)">
              {{ getRefuseMessage() }}
            </OmtTypography>
          </OmtCard>
        </CBox>

        <OmtRow v-if="pictures.length">
          <CGrid
            w="100%"
            :templateColumns="['repeat(auto-fill, minmax(74px, 1fr))', 'repeat(auto-fill, minmax(134px, 1fr))']"
            gap="6"
          >
            <ImagePreviewer v-for="(picture, index) in pictures" :key="index" v-bind="picture" />
          </CGrid>
        </OmtRow>
        <template v-else>
          <CBox v-for="(ticket, i) in refundDetail.list" :key="i">
            <!-- Devolutiva Omint -->
            <CStack
              v-if="['P', 'U'].includes(refundDetail.status)"
              px="16px"
              pt="16px"
              mb="16px"
              bg="var(--colors-light-primary-light-gray)"
              border-radius="8px"
            >
              <TextTile v-if="ticket.devolutionGroups.length > 1" v-chakra v-bind="{ title: 'Pendências' }" />

              <CBox v-for="(devolutionGroup, dgIndex) in ticket.devolutionGroups" :key="dgIndex">
                <OmtTypography v-if="ticket.devolutionGroups.length > 1" as="p1" mb="14px">
                  <omt-bold>{{ dgIndex + 1 }}. </omt-bold>
                  {{ devolutionGroup.Grupo }}
                </OmtTypography>
                <CFlex v-else>
                  <img :src="IconOmint" class="img-omint" />
                  <TextTile
                    v-chakra
                    v-bind="{ title: devolutionGroup.Grupo, color: 'var(--colors-light-primary-gray1)', as: 'h3-bold' }"
                  />
                </CFlex>

                <CBox mt="16px" v-for="(devolutionReason, drIndex) in devolutionGroup.Motivos" :key="drIndex">
                  <OmtTypography as="p1" mb="14px">
                    <omt-bold v-if="devolutionGroup.Motivos.length > 1">{{ drIndex + 1 }}. </omt-bold>
                    {{ devolutionReason }}
                  </OmtTypography>
                </CBox>
              </CBox>

              <OmtButton v-if="refundDetail.status === 'U'" @click="cancelPendency(ticket)" variant="ghost">
                <OmtIcons
                  font-size="16px"
                  name="trash-can"
                  mr="8px"
                  size="inherit"
                  color="var(--colors-light-support-error)"
                />
                <OmtTypography as="button-small" color="var(--colors-light-support-error)">
                  Cancelar solicitação
                </OmtTypography>
              </OmtButton>
            </CStack>

            <CDivider v-if="refundDetail.status === 'P'" mt="14px" mb="14px" />

            <CBox v-for="(refund, index) in ticket.docs" :key="index">
              <CBox data-cy="card-content">
                <CGrid
                  :templateColumns="['repeat(auto-fit, minmax(50%, max-content))']"
                  justify-content="space-between"
                >
                  <CGridItem>
                    <TextTile v-chakra mb="14px" v-bind="{ title: 'Prestador', description: refund.provider || '-' }" />
                  </CGridItem>
                  <CGridItem>
                    <TextTile
                      v-chakra
                      mb="14px"
                      v-bind="{
                        title: refund.createdAt ? 'Data do atendimento' : '',
                        description: refund.createdAt ? refund.createdAt : ''
                      }"
                    />
                  </CGridItem>
                  <CGridItem v-if="refundDetail.showValues && refund.requestedValue">
                    <TextTile
                      v-chakra
                      mb="14px"
                      v-bind="{ title: 'Valor apresentado', description: refund.requestedValue }"
                    />
                  </CGridItem>
                  <CGridItem v-if="refundDetail.status === 'L'">
                    <TextTile v-bind="{ title: 'Valor reembolsado', description: refund.refundedValue }" />
                  </CGridItem>
                  <CGridItem v-if="refundDetail.status === 'L'">
                    <TextTile v-bind="{ title: 'Data do crédito', description: refund.creditedAt }" />
                  </CGridItem>
                  <CGridItem v-if="refund.dscCompl" mb="5px">
                    <TextTile v-chakra v-bind="{ title: refund.dscCompl }" />
                  </CGridItem>
                  <CGridItem v-show="!!refund.receipts.length" v-for="(receipt, j) in refund.receipts" :key="j">
                    <OmtButton
                      height="18px"
                      p="0"
                      ml="10px"
                      align-items="self-end"
                      leftIcon="arrow-down-to-bracket"
                      variant="ghost"
                      iconColor="var(--colors-light-primary-navy-blue)"
                      :disabled="!receipt.source"
                      @click="download(receipt.id, receipt.source)"
                    >
                      <OmtTypography ml="-8px" as="button-small" color="var(--colors-light-primary-navy-blue)">
                        {{ receipt.label }}
                      </OmtTypography>
                    </OmtButton>
                  </CGridItem>
                </CGrid>
              </CBox>

              <CDivider v-if="index + 1 < ticket.docs.length" mt="10px" mb="10px" />

              <CBox
                v-if="
                  refund.demonstrativo &&
                  refundDetail.status === 'L' &&
                  i + 1 === refundDetail.list.length &&
                  index + 1 === ticket.docs.length
                "
              >
              </CBox>
            </CBox>

            <CDivider v-if="i + 1 < refundDetail.list.length" mt="24px" mb="24px" />
          </CBox>
        </template>

        <slot />

        <!-- Seção de documentos complementares -->
        <template v-if="complementaryPictures && complementaryPictures.length">
          <CDivider mt="16px" mb="16px" />

          <OmtRow mb="16px">
            <CBox display="flex" align-items="center">
              <OmtIcons name="image" size="1x" mr="2" />
              <OmtTypography as="h2-bold"> Documentos complementares </OmtTypography>
            </CBox>
          </OmtRow>

          <OmtRow>
            <OmtTypography as="p1" mb="14px"> Pedidos médicos, relatórios e outros </OmtTypography>
          </OmtRow>

          <OmtRow>
            <CGrid
              w="100%"
              :templateColumns="['repeat(auto-fill, minmax(74px, 1fr))', 'repeat(auto-fill, minmax(134px, 1fr))']"
              gap="6"
            >
              <ImagePreviewer v-for="(picture, index) in complementaryPictures" :key="index" v-bind="picture" />
            </CGrid>
          </OmtRow>
        </template>
      </CBox>
    </CCollapse>

    <OmtStickedModal v-if="showPictureModal" @close="triggerPictureModal(false)">
      <CBox p="0 16px" min-height="80vh">
        <CImage data-cy="image-preview-modal" :src="sourcePictureModal" max-height="100vh" max-width="100%" />
      </CBox>
    </OmtStickedModal>
  </OmtCard>
</template>

<script>
import ImagePreviewer from "@/components/Molecules/ImagePreviewer";
import TextTile from "@/components/Molecules/TextTile";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { mapActions } from "vuex";
import { StatusTile } from "./shared";

/**
 * ```ts
 * type refundType = {
 *  status: 'L' | 'R' | 'P' | 'A' | 'C' | 'U'
 *  total: string
 *  showValues: boolean
 *  pictures: { id: number, source: string, loadModalImage: Function }[]
 *  list: {
 *    provider: string
 *    createdAt: string
 *    creditedAt?: string
 *    requestedValue: string
 *    refundedValue?: string
 *    reasonPendencieRefused string
 *    receipts: { label: string, source: string }[]
 *  }
 * }
 * ```
 * @prop { refundType } refundDetail
 */
export default {
  components: {
    TextTile,
    StatusTile,
    ImagePreviewer
  },
  props: {
    refundDetail: Object,
    cancelPendency: Function,
    isCollapsed: Boolean,
    complementaryPictures: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOpen: true,
      showPictureModal: false,
      sourcePictureModal: "",
      pictures: []
    };
  },
  computed: {
    IconOmint() {
      return process.env.VUE_APP_BASE_URL + "images/Group3305.png";
    },
    isDenied() {
      // ajuste conforme regra real do back
      return ["D", "R"].includes(this.refundDetail?.status);
    }
  },
  mounted() {
    this.loadPictures();
  },
  methods: {
    ...mapActions("refund", ["fetchProtocolDetailImage"]),
    getRefuseMessage() {
      const list = this.refundDetail?.list || [];
      const first = list[0] || {};
      const groups = first.devolutionGroups || [];
      const reasons = [];
      groups.forEach(g => {
        if (g && Array.isArray(g.Motivos) && g.Motivos.length) {
          g.Motivos.forEach(m => reasons.push(m));
        } else if (g && g.Grupo) {
          reasons.push(g.Grupo);
        }
      });
      if (reasons.length) return reasons.join(" ");
      return first.reasonPendencieRefused || this.refundDetail?.reasonPendencieRefused || "";
    },
    loadPictures() {
      this.refundDetail.pictures.forEach(p =>
        this.fetchProtocolDetailImage({ documento: p.id, original: false }).then(source =>
          this.pictures.push({
            id: p.id,
            source: source,
            loadModalImage: () => this.loadPicture(p.id)
          })
        )
      );
    },
    async loadPicture(id) {
      return await this.fetchProtocolDetailImage({ documento: id, original: true });
    },
    triggerPictureModal(value) {
      this.showPictureModal = value;
    },
    async download(id, link) {
      if (!link) return;
      try {
        await this.$root.$emit("show-loading");
        this.sourcePictureModal = await this.fetchProtocolDetailImage({ documento: id, original: true });
        if (this.sourcePictureModal) {
          this.triggerPictureModal(true);
          return;
        }
      } finally {
        await this.$root.$emit("hide-loading");
      }
      await Swal.fire({
        text: "A imagem não está disponível.",
        confirmButtonText: "OK"
      });
    },
    getHeader() {
      let totalRefundedValue = null;
      let count = 0;
      if (this.refundDetail.showValues && this.refundDetail.status !== "L") {
        totalRefundedValue = this.refundDetail.total;
      }
      if (this.refundDetail.list.length) {
        this.refundDetail.list.map(t => {
          count += t.docs.length;
        });
      }
      return {
        count,
        total: totalRefundedValue,
        label: this.refundDetail.description,
        color: this.refundDetail.color
      };
    }
  }
};
</script>

<style scoped>
.img-omint {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}
</style>
